import { reactive } from 'vue';
import Swal from 'sweetalert2';
import axios from 'axios';

// ========================================
// 환경 감지 및 설정
// ========================================

// 환경 감지 유틸리티
class EnvironmentDetector {
    static isElectron() {
        return !!(window && window.process && window.process.type) ||
            !!(window && window.electronAPI && window.electronAPI.isElectron);
    }

    static isProduction() {
        return process.env.NODE_ENV === 'production';
    }

    static isLocal() {
        return window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';
    }

    static isDevelopment() {
        return process.env.NODE_ENV === 'development';
    }
}

// 환경별 설정 관리자
class ConfigurationManager {
    static getConfig() {
        const env = {
            isElectron: EnvironmentDetector.isElectron(),
            isProduction: EnvironmentDetector.isProduction(),
            isDevelopment: EnvironmentDetector.isDevelopment(),
            isLocal: EnvironmentDetector.isLocal()
        };

        if (env.isElectron) {
            return this.getElectronConfig(env.isProduction);
        } else {
            return this.getWebConfig(env.isProduction);
        }
    }

    static getElectronConfig(isProduction) {
        const baseConfig = {
            baseURL: 'https://xn--ok0b54rgwlnwitkg.com',
            hanq: 'https://한큐.com',
            virtualaccount: 'http://54.180.98.150:3001',
            crawling: 'IPC_COMMUNICATION',
            hubServiceGo: 'http://54.180.119.81:9090',
            hubServiceNest: 'https://54.180.240.25:9443',
            s3Bucket: 'https://hanqmedi.s3.ap-northeast-2.amazonaws.com',
            useIPC: true
        };

        if (isProduction) {
            // 일렉트론 배포 환경
            console.log('일렉트론 배포 환경: 중앙 서버 사용');
            return {
                ...baseConfig,
                baseURL: 'https://xn--ok0b54rgwlnwitkg.com',
                hanqmedi: 'https://xn--ok0b54rgwlnwitkg.com/api'
            };
        } else {
            // 일렉트론 개발 환경
            console.log('일렉트론 개발 환경: 로컬 서버 사용');
            return {
                ...baseConfig,
                baseURL: 'http://127.0.0.1:3000',
                hanqmedi: 'http://127.0.0.1:3000/api'
            };
        }
    }

    static getWebConfig(isProduction) {
        if (isProduction) {
            // 웹 프로덕션 환경
            return {
                baseURL: 'https://xn--ok0b54rgwlnwitkg.com',
                hanq: 'https://한큐.com',
                virtualaccount: 'http://54.180.98.150:3001',
                hanqmedi: 'https://xn--ok0b54rgwlnwitkg.com/api',
                crawling: 'https://xn--ok0b54rgwlnwitkg.com/crawling',
                s3Bucket: 'https://hanqmedi.s3.ap-northeast-2.amazonaws.com',
                hubServiceGo: 'http://54.180.119.81:9090',
                hubServiceNest: 'https://54.180.240.25:9443',
                useIPC: false
            };
        } else {
            // 웹 개발 환경
            return {
                baseURL: 'http://localhost:3000',
                hanq: 'https://한큐.com',
                virtualaccount: 'http://54.180.98.150:3001',
                hanqmedi: 'http://localhost:3000/api',
                crawling: 'http://localhost:9000',
                s3Bucket: 'https://hanqmedi.s3.ap-northeast-2.amazonaws.com',
                hubServiceGo: 'http://54.180.119.81:9090',
                hubServiceNest: 'https://54.180.240.25:9443',
                useIPC: false
            };
        }
    }

    static getKakaoRedirectUri() {
        const env = {
            isElectron: EnvironmentDetector.isElectron(),
            isProduction: EnvironmentDetector.isProduction()
        };

        if (env.isElectron) {
            return env.isProduction
                ? 'https://xn--ok0b54rgwlnwitkg.com/login'
                : 'http://localhost:8080/login';
        } else {
            return window.location.origin + '/login';
        }
    }
}

// ========================================
// Sentry 로깅 유틸리티
// ========================================

/**
 * Sentry 로깅 유틸리티 클래스
 */
class SentryLogger {
    static log(level, message, extra = {}) {
        if (window.electronAPI && window.electronAPI.sentry) {
            const cleanExtra = this.cleanForIPC(extra);
            window.electronAPI.sentry.log(message, cleanExtra);
        } else {
            console.log(`[Sentry Disabled] ${level.toUpperCase()}: ${message}`, extra);
        }
    }

    static error(error, context = {}) {
        if (window.electronAPI && window.electronAPI.sentry) {
            const cleanError = {
                message: error.message || 'Unknown error',
                stack: error.stack || '',
                name: error.name || 'Error'
            };
            const cleanContext = this.cleanForIPC(context);
            window.electronAPI.sentry.error(cleanError, cleanContext);
        } else {
            console.error(`[Sentry Disabled] Error:`, error, context);
        }
    }

    static logWithComponent(component, level, message, extra = {}) {
        if (extra.logType === 'userAction' || extra.logType === 'apiRequest') {
            console.log(`[${component}] ${message}`, extra);
            return;
        }

        this.log(level, message, {
            component,
            ...extra
        });
    }

    static errorWithComponent(component, error, context = {}) {
        this.error(error, {
            component,
            ...context
        });
    }

    // IPC 통신을 위한 객체 정리 메서드
    static cleanForIPC(obj) {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj === 'function') {
            return '[Function]';
        }

        if (obj instanceof Date) {
            return obj.toISOString();
        }

        if (obj instanceof Error) {
            return {
                message: obj.message,
                stack: obj.stack,
                name: obj.name
            };
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.cleanForIPC(item));
        }

        if (typeof obj === 'object') {
            const cleaned = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    try {
                        cleaned[key] = this.cleanForIPC(obj[key]);
                    } catch (e) {
                        cleaned[key] = '[Unserializable]';
                    }
                }
            }
            return cleaned;
        }

        return obj;
    }
}

// ========================================
// 데이터 처리 유틸리티
// ========================================

/**
 * 텍스트 및 객체 인코딩/디코딩 유틸리티
 */
class DataProcessor {
    static decodeText(text) {
        try {
            return decodeURIComponent(escape(text));
        } catch (error) {
            return text;
        }
    }

    static decodeObject(obj) {
        if (typeof obj === 'string') {
            return this.decodeText(obj);
        } else if (Array.isArray(obj)) {
            return obj.map(item => this.decodeObject(item));
        } else if (obj !== null && typeof obj === 'object') {
            const decoded = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    decoded[key] = this.decodeObject(obj[key]);
                }
            }
            return decoded;
        }
        return obj;
    }

    static sanitizeForIPC(data) {
        if (data === null || data === undefined) {
            return data;
        }

        try {
            return JSON.parse(JSON.stringify(data));
        } catch (error) {
            console.warn('JSON 직렬화 실패, 수동으로 데이터 정리:', error);
            return this.cleanObjectForIPC(data);
        }
    }

    static cleanObjectForIPC(obj) {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj === 'function') return undefined;
        if (obj instanceof Date) return obj.toISOString();
        if (obj instanceof File || obj instanceof Blob) {
            throw new Error('파일 객체는 IPC 통신으로 전송할 수 없습니다.');
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.cleanObjectForIPC(item)).filter(item => item !== undefined);
        }

        if (typeof obj === 'object') {
            const cleaned = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const value = this.cleanObjectForIPC(obj[key]);
                    if (value !== undefined) {
                        cleaned[key] = value;
                    }
                }
            }
            return cleaned;
        }

        return obj;
    }
}

// ========================================
// 파일 처리 유틸리티
// ========================================

/**
 * 파일 처리 및 압축 유틸리티
 */
class FileProcessor {
    static fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    static compressImage(file, maxWidth = 800, maxHeight = 600, quality = 0.6) {
        return new Promise((resolve, reject) => {
            // PDF 파일 처리
            if (file.type === 'application/pdf') {
                if (file.size > 30 * 1024 * 1024) {
                    reject(new Error(`PDF 파일 크기가 너무 큽니다: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB > 30MB)`));
                    return;
                }
                resolve(file);
                return;
            }

            // 이미지가 아닌 파일 처리
            if (!file.type.startsWith('image/')) {
                if (file.size > 30 * 1024 * 1024) {
                    reject(new Error(`비이미지 파일 크기가 너무 큽니다: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB > 30MB)`));
                    return;
                }
                resolve(file);
                return;
            }

            // 이미지 압축 처리
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                let { width, height } = img;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        if (blob.size > 1 * 1024 * 1024) {
                            canvas.toBlob((secondBlob) => {
                                const finalBlob = secondBlob && secondBlob.size < blob.size ? secondBlob : blob;
                                const compressedFile = new File([finalBlob], file.name, {
                                    type: finalBlob.type,
                                    lastModified: Date.now()
                                });
                                resolve(compressedFile);
                            }, file.type, 0.3);
                        } else {
                            const compressedFile = new File([blob], file.name, {
                                type: blob.type,
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        }
                    } else {
                        resolve(file);
                    }
                }, file.type, quality);
            };

            img.onerror = () => resolve(file);
            img.src = URL.createObjectURL(file);
        });
    }

    static async processFormData(formData, isFileUploadAPI = false) {
        const formDataObject = {};
        const filePromises = [];
        let fileCount = 0;

        // 파일 업로드 API별 처리 로직
        const maxFileSize = isFileUploadAPI ? 50 * 1024 * 1024 : 30 * 1024 * 1024;
        const compressionSettings = isFileUploadAPI
            ? { maxWidth: 1200, maxHeight: 900, quality: 0.8 }
            : { maxWidth: 800, maxHeight: 600, quality: 0.6 };

        for (const [key, value] of formData.entries()) {
            if (value instanceof File) {
                fileCount++;

                if (value.size > maxFileSize) {
                    throw new Error(`파일 크기가 너무 큽니다: ${value.name} (${(value.size / 1024 / 1024).toFixed(2)}MB > ${maxFileSize / 1024 / 1024}MB)`);
                }

                const filePromise = this.compressImage(value, compressionSettings.maxWidth, compressionSettings.maxHeight, compressionSettings.quality)
                    .then(async (processedFile) => {
                        if (isFileUploadAPI) {
                            const base64Data = await this.fileToBase64(processedFile);
                            formDataObject[key] = {
                                fileName: processedFile.name,
                                fileType: processedFile.type,
                                fileSize: processedFile.size,
                                originalSize: value.size,
                                base64Size: base64Data.length,
                                fileData: base64Data,
                                isFile: true
                            };
                        } else {
                            const base64Data = await this.fileToBase64(processedFile);
                            formDataObject[key] = base64Data;
                        }
                    });

                filePromises.push(filePromise);
            } else {
                // 일반 데이터 처리
                if (value === null || value === undefined) {
                    formDataObject[key] = '';
                } else if (typeof value === 'object') {
                    formDataObject[key] = JSON.stringify(value);
                } else {
                    formDataObject[key] = String(value);
                }
            }
        }

        if (filePromises.length > 0) {
            await Promise.all(filePromises);
            console.log('✅ 파일 처리 완료');
        }

        return { formDataObject, fileCount };
    }
}

// ========================================
// HTTP 클라이언트
// ========================================

/**
 * 일렉트론 전용 HTTP 클라이언트
 */
class ElectronHttpClient {
    static async request(config) {
        if (!window.electronAPI || !window.electronAPI.apiRequest) {
            throw new Error('일렉트론 API가 사용할 수 없습니다.');
        }

        try {
            const token = localStorage.getItem('token');
            const headers = { ...config.headers };

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            let processedData = config.data;
            let processedHeaders = headers;

            // FormData 처리
            if (config.data instanceof FormData) {
                const result = await this.handleFormData(config);
                if (result.handled) {
                    return result.response;
                }
                processedData = result.processedData;
                processedHeaders = result.processedHeaders;
            } else {
                processedData = DataProcessor.sanitizeForIPC(processedData);
            }

            processedHeaders = DataProcessor.sanitizeForIPC(processedHeaders);

            // URL 파라미터 처리
            let url = config.url;
            if (config.params && Object.keys(config.params).length > 0) {
                const searchParams = new URLSearchParams();
                for (const [key, value] of Object.entries(config.params)) {
                    if (value !== null && value !== undefined) {
                        searchParams.append(key, String(value));
                    }
                }
                const queryString = searchParams.toString();
                if (queryString) {
                    url += (url.includes('?') ? '&' : '?') + queryString;
                }
            }

            // SSL 설정
            let sslOptions = null;
            if (url.startsWith('https://')) {
                sslOptions = {
                    rejectUnauthorized: false,
                    requestCert: false,
                    agent: false
                };
            }

            // IPC 통신으로 API 요청
            const result = await window.electronAPI.apiRequest(
                config.method || 'get',
                url,
                processedData,
                processedHeaders,
                config.timeout || 30000,
                sslOptions
            );

            if (result.success) {
                const decodedData = DataProcessor.decodeObject(result.data);
                return {
                    data: decodedData,
                    status: result.status || 200,
                    statusText: 'OK',
                    headers: result.headers || {},
                    config: config,
                    request: null
                };
            } else {
                const error = new Error(result.error || 'API 요청 실패');
                error.response = {
                    data: result.data,
                    status: result.status || 500,
                    statusText: 'Internal Server Error',
                    headers: {},
                    config: config
                };
                error.config = config;
                error.code = 'ERR_BAD_RESPONSE';
                error.name = 'AxiosError';
                throw error;
            }
        } catch (error) {
            throw this.createAxiosError(error, config);
        }
    }

    static async handleFormData(config) {
        const dedicatedFileUploadAPIs = [
            '/documents/upload',
            '/files/upload',
            '/attachments/upload',
            'orderImageUpload',
            'uploadExcel',
            'uploadRecipientFile'
        ];

        const isDedicatedFileUpload = dedicatedFileUploadAPIs.some(api => config.url.includes(api));

        // 팩스 전송 API는 원본 axios 사용
        if (config.url.includes('/popbill/fax/sendFax')) {
            return {
                handled: true,
                response: await axios(config)
            };
        }

        if (config.url.includes('/popbill/mms/sendMMS')) {
            return {
                handled: true,
                response: await axios(config)
            };
        }

        if (config.url.includes('/registerClient')) {
            return {
                handled: true,
                response: await axios(config)
            };
        }

        if (isDedicatedFileUpload && window.electronAPI && window.electronAPI.uploadFile) {
            // 전용 파일 업로드 API 처리
            const { formDataObject } = await FileProcessor.processFormData(config.data, true);

            const totalDataString = JSON.stringify(formDataObject);
            const totalSize = totalDataString.length;

            if (totalSize > 50 * 1024 * 1024) {
                throw new Error(`전체 전송 데이터가 너무 큽니다: ${(totalSize / 1024 / 1024).toFixed(2)}MB > 50MB`);
            }

            // 업로드 타입 결정
            let uploadType = 'documents';
            let uploadConfig = {};

            if (config.url.includes('orderImageUpload')) {
                uploadType = 'orderImages';
                const urlParts = config.url.split('/');
                const orderId = urlParts[urlParts.length - 1];
                uploadConfig.orderId = orderId;
            } else if (config.url.includes('uploadRecipientFile')) {
                uploadType = 'recipientFiles';
                // 수급자 파일 업로드 설정
                uploadConfig.recipientId = formDataObject.recipientId;
                uploadConfig.recipientName = formDataObject.recipientName;
            }

            const uploadResult = await window.electronAPI.uploadFile(uploadType, formDataObject, uploadConfig);

            if (uploadResult.success) {
                return {
                    handled: true,
                    response: {
                        data: uploadResult.data,
                        status: 200,
                        statusText: 'OK',
                        headers: {},
                        config: config,
                        request: null
                    }
                };
            } else {
                const error = new Error(uploadResult.error || '파일 업로드 실패');
                error.response = {
                    data: uploadResult.data,
                    status: uploadResult.status || 500,
                    statusText: 'Internal Server Error',
                    headers: {},
                    config: config
                };
                throw error;
            }
        } else {
            // 일반 FormData 처리
            const { formDataObject } = await FileProcessor.processFormData(config.data, false);

            return {
                handled: false,
                processedData: formDataObject,
                processedHeaders: {
                    ...config.headers,
                    'Content-Type': 'application/json'
                }
            };
        }
    }

    static createAxiosError(error, config) {
        if (error.response) {
            return error;
        }

        const axiosError = new Error(error.message || 'IPC 통신 실패');
        axiosError.response = {
            data: null,
            status: 500,
            statusText: 'Internal Server Error',
            headers: {},
            config: config
        };
        axiosError.config = config;
        axiosError.code = 'ERR_NETWORK';
        axiosError.name = 'AxiosError';
        return axiosError;
    }

    static async get(url, config = {}) {
        return this.request({ ...config, method: 'get', url });
    }

    static async post(url, data, config = {}) {
        return this.request({ ...config, method: 'post', url, data });
    }

    static async put(url, data, config = {}) {
        return this.request({ ...config, method: 'put', url, data });
    }

    static async delete(url, config = {}) {
        return this.request({ ...config, method: 'delete', url });
    }
}

/**
 * 웹 환경용 HTTP 클라이언트 설정
 */
class WebHttpClient {
    static setupAxios() {
        // SSL 검증 비활성화 (개발 환경용)
        if (typeof process !== 'undefined' && process.env && typeof window === 'undefined') {
            process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = "0";
        }

        // 요청 인터셉터
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // 응답 인터셉터
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                console.error('API 에러:', error.config && error.config.url ? error.config.url : 'Unknown URL', error.message);

                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('token');
                    if (typeof window !== 'undefined') {
                        window.location.href = '/login';
                    }
                }

                return Promise.reject(error);
            }
        );

        return axios;
    }
}

/**
 * HTTP 클라이언트 팩토리
 */
class HttpClientFactory {
    static create() {
        const isElectronAvailable = () => {
            return typeof window !== 'undefined' &&
                window.electronAPI &&
                typeof window.electronAPI.apiRequest === 'function';
        };

        if (isElectronAvailable()) {
            // 일렉트론 환경
            const electronClient = {
                request: ElectronHttpClient.request.bind(ElectronHttpClient),
                get: ElectronHttpClient.get.bind(ElectronHttpClient),
                post: ElectronHttpClient.post.bind(ElectronHttpClient),
                put: ElectronHttpClient.put.bind(ElectronHttpClient),
                delete: ElectronHttpClient.delete.bind(ElectronHttpClient)
            };

            // Proxy로 함수 호출 지원
            return new Proxy(electronClient.request, {
                apply(target, thisArg, argumentsList) {
                    return target(argumentsList[0]);
                },
                get(target, prop) {
                    if (typeof electronClient[prop] === 'function') {
                        return electronClient[prop];
                    }
                    return electronClient[prop];
                }
            });
        } else {
            // 웹 환경
            return WebHttpClient.setupAxios();
        }
    }
}

// ========================================
// 크롤링 API 관리자
// ========================================

/**
 * 크롤링 API 요청 관리자
 */
class CrawlingManager {
    constructor(config) {
        this.config = config;
    }

    async request(endpoint, data, options = {}) {
        const { timeout = 60000 } = options;

        if (this.config.useIPC && window.electronAPI && window.electronAPI.isElectron) {
            // 일렉트론 환경: IPC 통신 사용
            console.log('🔄 IPC 통신으로 크롤링 요청:', endpoint);

            try {
                const result = await window.electronAPI.crawlingRequest(endpoint, data, timeout);
                if (!result.success) {
                    throw new Error(result.error || '크롤링 요청 실패');
                }
                return result.data;
            } catch (error) {
                console.error('❌ IPC 크롤링 요청 실패:', error);
                throw error;
            }
        } else {
            // 웹 환경: HTTP 요청 사용
            console.log('🔄 HTTP 요청으로 크롤링 요청:', endpoint);

            try {
                const response = await axios.post(
                    `${this.config.crawling}${endpoint}`,
                    data,
                    {
                        timeout,
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (!response.data.success) {
                    throw new Error(response.data.message || '크롤링 요청 실패');
                }
                return response.data;
            } catch (error) {
                console.error('❌ HTTP 크롤링 요청 실패:', error);
                throw error;
            }
        }
    }

    // 산소발생기 청구등록
    async oxygenSubmitClaim(data) {
        return await this.request('/api/oxygenSubmitClaim', data, { timeout: 300000 });
    }

    // 산소발생기 청구위임내역등록
    async oxygenSubmitClaimList(data) {
        return await this.request('/api/oxygenSubmitClaimList', data, { timeout: 300000 });
    }

    // 당뇨소모성재료 청구등록
    async diabetesSubmitClaim(data) {
        return await this.request('/api/diabetesSubmitClaim', data, { timeout: 300000 });
    }

    // 당뇨소모성재료 청구위임내역등록
    async diabetesSubmitClaimList(data) {
        return await this.request('/api/diabetesSubmitClaimList', data, { timeout: 300000 });
    }

    // 보조기기 청구등록
    async assistanceSubmitClaim(data) {
        return await this.request('/api/assistanceSubmitClaim', data, { timeout: 300000 });
    }

    // 파일 업로드 (IPC 통신)
    async uploadFile(type, formData) {
        if (this.config.useIPC && window.electronAPI && window.electronAPI.isElectron) {
            console.log('📁 IPC 통신으로 파일 업로드:', type);
            try {
                const result = await window.electronAPI.uploadFile(type, formData);
                if (!result.success) {
                    throw new Error(result.error || '파일 업로드 실패');
                }
                return result.data;
            } catch (error) {
                console.error('❌ IPC 파일 업로드 실패:', error);
                throw error;
            }
        } else {
            throw new Error('❌ 이 환경에서는 파일 업로드가 지원되지 않습니다.');
        }
    }
}

// ========================================
// 전역 상태 관리
// ========================================

/**
 * 전역 상태 관리자
 */
class GlobalStateManager {
    static createState() {
        const globalState = reactive({
            company_key: '', // 사업소 키
            id: '', // 사용자 아이디
            manager_name: '', // 사용자 이름
            company: '', // 사업소 이름

            // 추가된 필드들
            company_bnum: '', // 사업자등록번호
            company_ceo_name: '', // 대표자명
            company_address: '', // 회사 주소
            company_type: '', // 업태
            company_industry: '', // 종목
            company_email: '', // 회사 이메일
            user_name: '', // 사용자 이름
            user_phone: '', // 사용자 전화번호
            company_phone: '', // 회사 전화번호`
            bank_name: '', // 은행명
            bank_number: '', // 계좌번호
            bank_username: '', // 예금주명

            // 기존 필드들
            popbill_id: '', // 사업소 팝빌 아이디
            popbill_api_key: '', // 사업소 팝빌 API 키
            thebill_id: '', // 사업소 더빌 아이디
            barobill_id: '', // 사업소 바로빌 아이디
            barobill_api_key: '', // 사업소 바로빌 API 키
            company_certificate_id: '', // 공인인증서 이름
            company_certificate_pw: '', // 공인인증서 비밀번호
            company_fax: '', // 사업소 팩스 번호
            is_electron: EnvironmentDetector.isElectron(),
            is_production: EnvironmentDetector.isProduction()
        });

        // localStorage에서 사용자 정보 복원
        this.restoreUserInfo(globalState);

        return globalState;
    }

    static restoreUserInfo(globalState) {
        try {
            const userInfoRaw = localStorage.getItem('userInfo');
            if (userInfoRaw) {
                const userInfo = JSON.parse(userInfoRaw);
                const decodedUserInfo = DataProcessor.decodeObject(userInfo);

                if (decodedUserInfo.company_key) {
                    // 기본 필드들
                    globalState.company_key = decodedUserInfo.company_key;
                    globalState.id = decodedUserInfo.id || '';
                    globalState.manager_name = decodedUserInfo.name || '';
                    globalState.company = decodedUserInfo.company || '';

                    // 모든 company_ 관련 필드들을 동적으로 설정
                    Object.keys(decodedUserInfo).forEach(key => {
                        if (key.startsWith('company_') || key === 'user_name') {
                            globalState[key] = decodedUserInfo[key] || '';
                        }
                    });

                    // 기타 필드들
                    globalState.popbill_id = decodedUserInfo.popbill_id || '';
                    globalState.popbill_api_key = decodedUserInfo.popbill_api_key || '';
                    globalState.thebill_id = decodedUserInfo.thebill_id || '';
                    globalState.barobill_id = decodedUserInfo.barobill_id || '';
                    globalState.barobill_api_key = decodedUserInfo.barobill_api_key || '';

                    globalState.bank_name = decodedUserInfo.bank_name || '';
                    globalState.bank_number = decodedUserInfo.bank_number || '';
                    globalState.bank_username = decodedUserInfo.bank_username || '';
                }
            }
        } catch (error) {
            console.warn('localStorage userInfo 복원 실패:', error);
        }
    }
}

// ========================================
// 포맷팅 유틸리티
// ========================================

/**
 * 포맷팅 유틸리티
 */
class FormatUtils {
    static price(price) {
        return new Intl.NumberFormat('ko-KR', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    }

    static dateString(dateString) {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    static dateType(dateType) {
        const year = dateType.getFullYear();
        const month = String(dateType.getMonth() + 1).padStart(2, '0');
        const day = String(dateType.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}

// ========================================
// 이미지 URL 헬퍼
// ========================================

/**
 * 이미지 URL 생성 헬퍼
 */
class ImageUrlHelper {
    constructor(s3BucketUrl) {
        this.s3BucketUrl = s3BucketUrl;
    }

    getImageUrl(imagePath, folder = 'Product-image') {
        if (!imagePath) return '';

        // 이미 완전한 URL인 경우 그대로 반환
        if (imagePath.startsWith('http')) {
            return imagePath;
        }

        // 슬래시 정리 후 URL 조합
        const cleanImagePath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
        const cleanFolder = folder.endsWith('/') ? folder.substring(0, folder.length - 1) : folder;

        return `${this.s3BucketUrl}/${cleanFolder}/${cleanImagePath}`;
    }

    getProductImageUrl(imageName) {
        return this.getImageUrl(imageName, 'Product-image');
    }
}

// ========================================
// 상태 스타일 헬퍼
// ========================================

/**
 * 배송 및 대여 상태 관련 헬퍼
 */
class StatusHelper {
    static getDeliveryMethod(delivery_method) {
        const methods = {
            1: '직접전달',
            2: '택배',
            3: '물류',
            4: '소독업체',
            5: '공급업체'
        };
        return methods[delivery_method] || '';
    }

    static getDeliveryStatus(delivery_status) {
        const statuses = {
            1: '배송완료',
            2: '배송대기',
            3: '회수중',
            4: '회수완료',
            5: '배송중'
        };
        return statuses[delivery_status] || '';
    }

    static getDeliveryStatusStyle(delivery_status) {
        const styles = {
            1: { color: '#27ae60' },  // 배송완료: 초록색
            2: { color: '#000000' },  // 배송대기: 검정색
            3: { color: '#FF9800' },  // 회수중: 주황색
            4: { color: '#c0392b' },  // 회수완료: 빨간색
            5: { color: '#f39c12' }   // 배송중: 노란색
        };
        return styles[delivery_status] || {};
    }

    static getRentalStatus(rental_status) {
        const statuses = {
            0: '대여중',
            1: '회수',
            2: '중지'
        };
        return statuses[rental_status] || '';
    }

    static getRentalStatusStyle(rental_status) {
        const styles = {
            0: 'color: #27ae60',  // 초록색 - 대여중
            1: 'color: #f39c12',  // 노란색 - 회수
            2: 'color: #c0392b'   // 빨간색 - 중지
        };
        return styles[rental_status] || 'color: #666666';
    }

    static getStatus(status) {
        const statuses = {
            0: '전체',
            1: '미확정',
            2: '확정',
            3: '출고'
        };
        return statuses[status] || '';
    }
}

// ========================================
// 메인 플러그인
// ========================================

/**
 * Vue 플러그인 메인 클래스
 */
export default {
    install: (app) => {
        // 환경 설정 로드
        const envConfig = ConfigurationManager.getConfig();

        // HTTP 클라이언트 생성
        const httpClient = HttpClientFactory.create();

        // 전역 상태 생성
        const globalState = GlobalStateManager.createState();

        // 크롤링 매니저 생성
        const crawlingManager = new CrawlingManager(envConfig);

        // 이미지 URL 헬퍼 생성
        const imageUrlHelper = new ImageUrlHelper(envConfig.s3Bucket);

        // ========================================
        // Vue 전역 속성 등록
        // ========================================

        // 배송 관련 헬퍼
        app.config.globalProperties.$delivery_method = StatusHelper.getDeliveryMethod;
        app.config.globalProperties.$delivery_status = StatusHelper.getDeliveryStatus;
        app.config.globalProperties.$delivery_status_style = StatusHelper.getDeliveryStatusStyle;

        // 대여 관련 헬퍼
        app.config.globalProperties.$rental_status = StatusHelper.getRentalStatus;
        app.config.globalProperties.$rental_status_style = StatusHelper.getRentalStatusStyle;

        // 일반 상태 헬퍼
        app.config.globalProperties.$status = StatusHelper.getStatus;

        // 사업소 이름
        app.config.globalProperties.$company = globalState.company;

        // 팩스 번호
        app.config.globalProperties.$fax = globalState.company_fax;

        // 팝빌 아이디
        app.config.globalProperties.$popbill = globalState.popbill_id;

        // 팝빌 API 키
        app.config.globalProperties.$popbill_api_key = globalState.popbill_api_key;

        // 더빌 아이디
        app.config.globalProperties.$thebill = globalState.thebill_id;

        // 바로빌 아이디
        app.config.globalProperties.$barobill = globalState.barobill_id;

        // 바로빌 API 키
        app.config.globalProperties.$barobill_api_key = globalState.barobill_api_key;

        // 공인인증서 이름
        app.config.globalProperties.$certificate_id = globalState.company_certificate_id;

        // 공인인증서 비밀번호
        app.config.globalProperties.$certificate_pw = globalState.company_certificate_pw;

        // 은행명
        app.config.globalProperties.$bank_name = globalState.bank_name;

        // 계좌번호
        app.config.globalProperties.$bank_number = globalState.bank_number;

        // 예금주명
        app.config.globalProperties.$bank_username = globalState.bank_username;

        // 전역 상태 관리
        app.config.globalProperties.$globalState = globalState;
        app.config.globalProperties.$setGlobalState = (key, value) => {
            if (key in globalState) {
                const decodedValue = typeof value === 'string' ? DataProcessor.decodeText(value) : value;
                globalState[key] = decodedValue;
            }
        };

        // 포맷팅 유틸리티
        app.config.globalProperties.$format = {
            price: FormatUtils.price,
            dateString: FormatUtils.dateString,
            dateType: FormatUtils.dateType
        };

        // 이미지 URL 헬퍼
        app.config.globalProperties.$getImageUrl = imageUrlHelper.getImageUrl.bind(imageUrlHelper);
        app.config.globalProperties.$getProductImageUrl = imageUrlHelper.getProductImageUrl.bind(imageUrlHelper);

        // HTTP 클라이언트
        app.config.globalProperties.$axios = httpClient;

        // 환경 설정
        app.config.globalProperties.$apiUrls = {
            hanq: envConfig.hanq,
            virtualaccount: envConfig.virtualaccount,
            hanqmedi: envConfig.hanqmedi,
            getCompany: envConfig.getCompany,
            popbill: envConfig.popbill,
            crawling: envConfig.crawling,
            hubServiceGo: envConfig.hubServiceGo,
            hubServiceNest: envConfig.hubServiceNest,
            s3Bucket: envConfig.s3Bucket
        };

        // 환경 정보
        app.config.globalProperties.$env = {
            isElectron: EnvironmentDetector.isElectron(),
            isProduction: EnvironmentDetector.isProduction(),
            isLocal: EnvironmentDetector.isLocal(),
            current: process.env.VUE_APP_ENV || process.env.NODE_ENV
        };

        // 데이터 처리 유틸리티
        app.config.globalProperties.$decodeText = DataProcessor.decodeText;
        app.config.globalProperties.$decodeObject = DataProcessor.decodeObject;

        // 크롤링 API
        app.config.globalProperties.$crawling = {
            request: crawlingManager.request.bind(crawlingManager),
            oxygenSubmitClaim: crawlingManager.oxygenSubmitClaim.bind(crawlingManager),
            oxygenSubmitClaimList: crawlingManager.oxygenSubmitClaimList.bind(crawlingManager),
            diabetesSubmitClaim: crawlingManager.diabetesSubmitClaim.bind(crawlingManager),
            diabetesSubmitClaimList: crawlingManager.diabetesSubmitClaimList.bind(crawlingManager),
            assistanceSubmitClaim: crawlingManager.assistanceSubmitClaim.bind(crawlingManager),
            uploadFile: crawlingManager.uploadFile.bind(crawlingManager)
        };

        // 카카오 리다이렉트 URI
        app.config.globalProperties.$getKakaoRedirectUri = ConfigurationManager.getKakaoRedirectUri;

        // 환경 설정 함수
        app.config.globalProperties.$getEnvironmentConfig = ConfigurationManager.getConfig;

        // SweetAlert2
        app.config.globalProperties.$swal = Swal;

        // Sentry 로깅 유틸리티
        app.config.globalProperties.$sentry = SentryLogger;
        app.config.globalProperties.$logToSentry = SentryLogger.log;
        app.config.globalProperties.$logErrorToSentry = SentryLogger.error;
        app.config.globalProperties.$logUserAction = SentryLogger.userAction;
        app.config.globalProperties.$logApiRequest = SentryLogger.apiRequest;

        // SweetAlert2 로딩 기능 추가
        app.config.globalProperties.$swalLoading = {
            show: (message = '로딩 중...') => {
                Swal.fire({
                    title: message,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });
            },
            hide: () => {
                Swal.close();
            },
            update: (message) => {
                Swal.getTitle().textContent = message;
            }
        };

        // ========================================
        // 환경별 초기화
        // ========================================

        // 환경 정보 로깅
        const envInfo = {
            isElectron: EnvironmentDetector.isElectron(),
            isProduction: EnvironmentDetector.isProduction(),
            isDevelopment: EnvironmentDetector.isDevelopment(),
            isLocal: EnvironmentDetector.isLocal()
        };

        // SSL 검증 비활성화 알림 (개발 환경용)
        if (!envInfo.isProduction) {
            console.log('SSL 검증 비활성화 (개발 환경)');
        }
    }
};